import { parseCssLength } from "@hightouch/lib/ad-studio/psd-export/css-length";

const TRANSFORM_ORIGIN_CSS_KEY = "transform-origin";

/** Keyword → fraction of the box, per axis. `center` is valid on both. */
const X_KEYWORDS: Record<string, number> = { left: 0, center: 0.5, right: 1 };
const Y_KEYWORDS: Record<string, number> = { top: 0, center: 0.5, bottom: 1 };

export type TransformOrigin = { x: number; y: number };

/**
 * The pivot in the box's own coordinates. Absent, three-value, or unparseable falls back to the
 * center (CSS's default), so an exotic unit degrades to today's behavior rather than to a corner.
 */
export function resolveTransformOrigin(args: {
  extraStyles: Record<string, string> | undefined;
  width: number;
  height: number;
  /** Basis for `em` values. Absent → `em` is unparseable, so the pivot falls back to the center. */
  fontSize?: number;
}): TransformOrigin {
  const { extraStyles, width, height, fontSize } = args;
  const center = { x: width / 2, y: height / 2 };
  const raw = extraStyles?.[TRANSFORM_ORIGIN_CSS_KEY]?.trim().toLowerCase();
  if (!raw) return center;
  const tokens = raw.split(/\s+/);
  if (tokens.length < 1 || tokens.length > 2) return center;
  const [first, second = "center"] = tokens;
  if (!first) return center;
  // `top 40px` == `40px top`: a y-only keyword names its own axis.
  const swapped =
    Object.hasOwn(Y_KEYWORDS, first) && !Object.hasOwn(X_KEYWORDS, first);
  const xToken = swapped ? second : first;
  const yToken = swapped ? first : second;
  const x = resolveComponent(xToken, X_KEYWORDS, width, fontSize);
  const y = resolveComponent(yToken, Y_KEYWORDS, height, fontSize);
  if (x === null || y === null) return center;
  return { x, y };
}

function resolveComponent(
  token: string,
  keywords: Record<string, number>,
  size: number,
  fontSize: number | undefined,
): number | null {
  // `Object.hasOwn` because the token is authored markup — `constructor` must not resolve.
  if (Object.hasOwn(keywords, token)) return (keywords[token] ?? 0) * size;
  return parseCssLength(token, { percent: size, em: fontSize });
}
